import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subscription }                                    from 'rxjs';
import { TranslateService }                                from '@ngx-translate/core';

import { CompanyService }                from '../companies/company.service';
import { IntelligenceFormatService }     from './intelligence.format.service';
import { IntelligenceNarrativeService }  from './intelligence.narrative.service';
import { IntelligenceRangeModel }        from './typings/intelligence.range';
import { IntelligenceService }           from './intelligence.service';
import { SessionService }                from '../sessions/session.service';

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

@Component({
  encapsulation: ViewEncapsulation.None,
  selector     : 'wor-intelligence',
  styles: [ require('./intelligence.component.scss') ],
  template: require('./intelligence.component.html')
})
export class IntelligenceComponent implements OnInit, OnDestroy {
  public activeTab        : string = 'overview';
  public emailError       : string = '';
  public emailOpen        : boolean = false;
  public emailRecipient   : string = '';
  public emailSending     : boolean = false;
  public emailSent        : string = '';
  public entitled         : boolean = false;
  public narrative        : string = '';
  public narrativeError   : string = '';
  public narrativeLoading : boolean = false;

  public tabs = [ 'overview', 'money', 'customers', 'vehicles', 'products', 'purchasing', 'mechanics', 'operations', 'opportunities' ];

  private narrativeGeneration : number = 0;
  private subscription        : Subscription;

  constructor (
    private companyService              : CompanyService,
    private intelligenceFormatService   : IntelligenceFormatService,
    private intelligenceNarrativeService: IntelligenceNarrativeService,
    public intelligenceService          : IntelligenceService,
    private sessionService              : SessionService,
    private translateService            : TranslateService
  ) {
    this.entitled = this.sessionService.isAdminUser() && this.companyService.hasIntelligence();

    if ( this.companyService.hasDealership() ) {
      this.tabs.push('dealership');
    }
  }

  ngOnInit () : void {
    // A summary or email panel belongs to one tab, over one period, under one set of
    // filters. Changing any of those retires them rather than leaving stale state.
    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.clearTabState());
    this.subscription.add(this.intelligenceService.filtersChanged.subscribe(() => this.clearNarrative()));
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  earliestDateLabel () : string {
    return this.intelligenceFormatService.fullDate(this.intelligenceService.earliestDate);
  }

  loadNarrative () : void {
    if ( this.narrativeLoading ) {
      return;
    }

    if ( this.narrative || this.narrativeError ) {
      this.clearNarrative();

      return;
    }

    const generation = ++this.narrativeGeneration;
    const tab = this.activeTab;

    this.narrativeLoading = true;

    this.intelligenceNarrativeService.summariseTab(tab)
    .then(narrative => {
      if ( generation !== this.narrativeGeneration ) {
        return;
      }

      // The plan's AI allowance is spent: say so, and offer the upgrade where there is
      // one, rather than reporting it as a summary that could not be generated.
      if ( narrative.limitReached ) {
        this.narrativeError = this.translateService.instant(narrative.upgradeAvailable
          ? 'INTELLIGENCE_SPACE.ASK.LIMIT_UPGRADE'
          : 'INTELLIGENCE_SPACE.ASK.LIMIT_REACHED');

        return;
      }

      this.narrative = narrative.text;

      if ( !narrative.text ) {
        this.narrativeError = this.translateService.instant('INTELLIGENCE_SPACE.OVERVIEW.NARRATIVE_EMPTY');
      }
    })
    .catch(() => {
      if ( generation === this.narrativeGeneration ) {
        this.narrativeError = this.translateService.instant('INTELLIGENCE_SPACE.OVERVIEW.NARRATIVE_ERROR');
      }
    })
    .finally(() => {
      if ( generation === this.narrativeGeneration ) {
        this.narrativeLoading = false;
      }
    });
  }

  onRangeChange ( range : IntelligenceRangeModel ) : void {
    this.intelligenceService.setRange(range);
  }

  print () : void {
    window.print();
  }

  selectTab ( tab : string ) : void {
    this.activeTab = tab;

    // The figures behind the summary and the email panel belong to the previous tab.
    this.clearTabState();
  }

  sendEmail () : void {
    if ( this.emailSending ) {
      return;
    }

    if ( !EMAIL_PATTERN.test(this.emailRecipient.trim()) ) {
      this.emailError = this.translateService.instant('INTELLIGENCE_SPACE.EMAIL.INVALID');

      return;
    }

    const recipient = this.emailRecipient.trim();

    this.emailError = '';
    this.emailSending = true;
    this.emailSent = '';

    this.intelligenceService.emailReport(this.activeTab, recipient)
    .then(() => {
      this.emailSent = recipient;
      this.emailRecipient = '';
    })
    .catch(() => {
      this.emailError = this.translateService.instant('INTELLIGENCE_SPACE.EMAIL.ERROR');
    })
    .finally(() => {
      this.emailSending = false;
    });
  }

  toggleEmail () : void {
    this.emailOpen = !this.emailOpen;

    if ( !this.emailOpen ) {
      this.resetEmail();
    }
  }

  private clearNarrative () : void {
    this.narrativeGeneration += 1;
    this.narrative = '';
    this.narrativeError = '';
    this.narrativeLoading = false;
  }

  private clearTabState () : void {
    this.clearNarrative();

    this.emailOpen = false;

    this.resetEmail();
  }

  private resetEmail () : void {
    this.emailError = '';
    this.emailRecipient = '';
    this.emailSending = false;
    this.emailSent = '';
  }
}
